import PageBrand from '@components/common/PageBrand'
import Grid from '@mui/material/Grid'
import React, { useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import BackgroundAnimation from '../components/common/BackgroundAnimation'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { Link, useHistory } from 'react-router-dom'
import { User } from '../api/types/users'
import { err, wait } from '../utils/functions'
import { deleteAccount, getUser, logout, updateUser, updateUsername } from '../api/user'
import AppContext from '../app/AppContext'
import SaveIcon from '@mui/icons-material/Save'
import { GlassButton, GlassCard, GlassOutlineButton, GlassPanel } from '@components/common/Glass'
import Cookies from 'js-cookie'
import { clearAuthStorage } from 'src/utils/helpers/authStorage'
import Alert, { AlertInput } from '@components/common/Alert'

// const DEFAULT_ICONS = [
//   '👽',
//   '🤖',
//   '🐵',
//   '🐶',
//   '🦊',
//   '🐱',
//   '🐴',
//   '🦄',
//   '🐮',
//   '🐷',
//   '🐭',
//   '🐹',
//   '🐰',
//   '🐻',
//   '🐨',
//   '🐼',
//   '🐔',
//   '🐸',
//   '🐲',
//   '🐙',
// ]

export default function Account(): JSX.Element {
  const theme = useTheme()
  const history = useHistory()

  const { appState, appReducer } = React.useContext(AppContext)

  const [user, setUser] = useState<User | undefined>(undefined)
  const [email, setEmail] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [showAdult, setShowAdult] = useState<boolean>(false)
  const [usernameLocked, setUsernameLocked] = useState<boolean>(true)
  const [usernameError, setUsernameError] = useState<string>('')
  const [updatingUsername, setUpdatingUsername] = useState<boolean>(false)
  const [alert, setAlert] = useState<AlertInput>({
    open: false,
    message: '',
  })

  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const inputSx = {
    '& .MuiInputBase-root': {
      background: '#ffffff',
      borderRadius: '14px',
      // border: '1px solid rgba(255,255,255,0.75)',
      boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
      backdropFilter: 'none',
    },
    '& .MuiInputBase-input': {
      py: 1.25,
    },
  } as const

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      appReducer({ type: 'jwt', payload: undefined })
      appReducer({ type: 'user', payload: undefined })
      Cookies.remove('uuid', { path: '' })
      clearAuthStorage()
      history.push('/login')
    }
  }

  useEffect(() => {
    if (user) return

    const userId = appState.user.id
    if (!userId) return

    getUser(userId)
      .then((res) => {
        if (res.success && res.data) {
          setUser(res.data)
          setShowAdult(res.data.showAdult || false)
          setEmail(res.data.email)
          setUsername(res.data.username)
        }
      })
      .catch((e) => err(e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateUsername = (value: string): string => {
    const parsed = value.trim()
    if (parsed.length < 3) return 'Username must be at least 3 characters.'
    if (/\s/.test(parsed)) return 'Username cannot contain spaces.'
    if (parsed.startsWith('.')) return "Username cannot start with '.'"
    if (parsed.endsWith('.')) return "Username cannot end with '.'"
    if (parsed.includes('..')) return "Username cannot contain consecutive '.'"
    if (!/^[a-zA-Z0-9._]+$/.test(parsed)) {
      return 'Username can only contain letters, numbers, ".", and "_".'
    }
    return ''
  }

  useEffect(() => {
    if (usernameLocked) {
      setUsernameError('')
      return
    }
    const timeout = setTimeout(() => {
      setUsernameError(validateUsername(username))
    }, 500)
    return () => clearTimeout(timeout)
  }, [username, usernameLocked])

  const saveUser = async () => {
    if (!user) return

    const newUser = {
      ...user,
      showAdult: showAdult,
    }

    if (!usernameLocked) {
      const usernameSuccess = await handleUpdateUsername()
      if (usernameSuccess) {
        newUser.username = username
        await wait(1000)
      } else {
        return
      }
    }

    updateUser(newUser)
      .then((res) => {
        if (res.success) {
          setAlert({
            open: true,
            message: 'Account settings updated',
            severity: 'success',
          })
        } else {
          setAlert({
            open: true,
            message: 'Encountered error. Try again later',
            severity: 'error',
          })
        }
      })
      .catch((e) => {
        setAlert({
          open: true,
          message: 'Encountered error. Try again later',
          severity: 'error',
        })
        err(e)
      })
  }

  const handleDeleteAccount = async () => {
    const ok = confirm('Delete your account permanently? This cannot be undone.')
    if (!ok) return

    try {
      const res = await deleteAccount()
      if (!res.success) {
        setAlert({
          message: res.message || 'Failed to delete account.',
          open: true,
          severity: 'error',
        })
        return
      }
      setAlert({ message: 'Account deleted.', open: true, severity: 'error' })
      history.push('/login')
    } catch (e) {
      err(e as Error)
      setAlert({
        message: 'Failed to delete account.',
        open: true,
        severity: 'error',
      })
    }
  }

  const handleUpdateUsername = async (): Promise<boolean> => {
    const localError = validateUsername(username)
    if (localError) {
      setUsernameError(localError)
      return false
    }
    setUpdatingUsername(true)
    return await updateUsername(username.trim())
      .then((result) => {
        setUpdatingUsername(false)
        if (!result.success) {
          const msg = result.message || 'Failed to update username.'
          setUsernameError(msg.includes('taken') ? 'Username taken' : msg)
          setAlert({
            open: true,
            message: msg.includes('taken') ? 'Username taken' : msg,
            severity: 'error',
          })
          return false
        }

        const nextUser = result.data?.user
        if (nextUser) {
          setUser((prev) => ({ ...(prev || {}), ...nextUser }))
          appReducer({
            type: 'user',
            payload: {
              ...(appState.user || {}),
              name: nextUser.username || username.trim(),
            },
          })
          setUsername(nextUser.username || username.trim())
        }

        setUsernameLocked(true)
        setUsernameError('')
        setAlert({
          open: true,
          message: 'Username updated.',
          severity: 'success',
        })
        return true
      })
      .catch((err) => {
        setUpdatingUsername(false)
        console.log(err.response.data)
        return false
      })
  }

  const canUpdateUsername =
    !usernameLocked &&
    !updatingUsername &&
    !usernameError &&
    username.trim().length > 0 &&
    username.trim() !== (user?.username || '').trim()

  return (
    <div>
      <BackgroundAnimation />
      <img id="pencilImg" src="trophy.png" alt="" style={{ display: 'none' }} />
      <Container
        sx={{
          pb: 6,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        maxWidth="sm"
      >
        <PageBrand />
        <Stack justifyContent="center" alignItems="center" spacing={2}>
          <GlassCard
            sx={{
              width: '100%',
              px: { xs: 1, sm: 3.5 },
              py: 2,
              pb: 4,
              borderRadius: '32px',
              borderColor: 'rgba(255,255,255,0.7)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <GlassOutlineButton
                variant="outlined"
                onClick={() => history.push('/')}
                sx={{
                  p: 1,
                  minWidth: 'auto',
                }}
              >
                <ArrowBackIosNewRoundedIcon fontSize="small" sx={{ mr: 0.25 }} />
              </GlassOutlineButton>
            </Box>
            <Grid container justifyContent="center" p={0} spacing={1}>
              <Grid item xs={12}>
                <Typography variant={fullScreen ? 'h4' : 'h4'} fontWeight={600}>
                  Account Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.75}>
                  Manage your profile and content preferences.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container justifyContent="center" spacing={1} mt={3}>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth={true}
                      sx={inputSx}
                      label="Email"
                      disabled={true}
                      value={email}
                    ></TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth={true}
                      disabled={usernameLocked}
                      sx={inputSx}
                      label="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      error={Boolean(usernameError)}
                      helperText={
                        <Typography variant="caption" fontWeight={500} fontSize={14}>
                          {usernameError || ' '}
                        </Typography>
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {usernameLocked ? (
                              <IconButton
                                edge="end"
                                onClick={() => setUsernameLocked(false)}
                                aria-label="Unlock username field"
                                size="small"
                              >
                                <LockRoundedIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <Button
                                size="small"
                                onClick={handleUpdateUsername}
                                disabled={!canUpdateUsername}
                              >
                                {updatingUsername ? 'Updating...' : 'Update'}
                              </Button>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    ></TextField>
                  </Grid>
                  {/* <Grid item xs={12}>
                    <TextField
                      rows={4}
                      multiline={true}
                      size="small"
                      fullWidth={true}
                      sx={inputSx}
                      placeholder="Bio (optional)"
                      // value={user?.bio}
                    ></TextField>
                  </Grid> */}
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <GlassPanel sx={{ p: 2, borderRadius: '16px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch checked={showAdult} onChange={() => setShowAdult(!showAdult)} />
                      }
                      label={
                        <Box pl={1}>
                          <Typography variant="body1" fontWeight={600} textAlign="left">
                            Show Sensitive Content
                          </Typography>
                        </Box>
                      }
                    />
                    <Typography variant="body2" color="#64748b" textAlign="left">
                      User-generated content may contain adult or inappropriate content.
                    </Typography>
                    <Typography variant="body2" color="#64748b" textAlign="left">
                      Only toggle if you are comfortable with that.
                    </Typography>
                  </FormGroup>
                </GlassPanel>
              </Grid>

              <Grid item xs={12} mt={2}>
                <GlassOutlineButton
                  variant="outlined"
                  size="large"
                  sx={{
                    minHeight: 48,
                    fontSize: 16,
                  }}
                  fullWidth
                  onClick={handleLogout}
                >
                  Logout
                </GlassOutlineButton>
              </Grid>

              <Grid item xs={12} mt={2}>
                <GlassButton
                  variant="contained"
                  size="large"
                  sx={{
                    minHeight: 48,
                    fontSize: 16,
                  }}
                  fullWidth
                  endIcon={<SaveIcon />}
                  onClick={() => saveUser()}
                >
                  Save
                </GlassButton>
              </Grid>

              <Grid item xs={12}>
                <Box py={4}>
                  <Divider />
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  sx={{
                    minHeight: 48,
                    fontSize: 16,
                  }}
                  fullWidth
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </Button>
              </Grid>
              {/* <Grid item xs={12} mt={6}>
              <Typography variant="h6">Friends</Typography>
              <Grid container justifyContent="center" spacing={4} pt={2}>
                <Grid item xs={12}>
                  <List
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #eee',
                    }}
                  >
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar></Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={'Jeremy'}
                        secondary={'jeremy123'}
                      ></ListItemText>
                      <ListItemSecondaryAction>
                        <IconButton>
                          <ClearOutlinedIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Grid> */}
            </Grid>
          </GlassCard>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Link
              to="/privacy"
              style={{
                textDecoration: 'none',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#60768b' }}>
                Privacy Policy
              </Typography>
            </Link>
            <Link
              to="/terms"
              style={{
                textDecoration: 'none',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#60768b' }}>
                Terms of Service
              </Typography>
            </Link>
          </Box>
        </Stack>
      </Container>
      <Alert alert={alert} onClose={() => setAlert({ ...alert, open: false })} />
    </div>
  )
}
